import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Grid2 as Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InsertEmoticonOutlinedIcon from "@mui/icons-material/InsertEmoticonOutlined";
import SendIcon from "@mui/icons-material/Send";
import EmojiPicker, {
  EmojiClickData,
  Theme as EmojiPickerTheme,
} from "emoji-picker-react";
import { useApolloClient } from "@apollo/client";
import { MainLayoutContainer } from "components/Layout";
import {
  GetChatMessagesDocument,
  GetChatsForUserDocument,
  useCreateChatWithParticipantsMutation,
  useDeleteChatByPkMutation,
  useDeleteChatMessageByPkMutation,
  useGetChatMessagesQuery,
  useGetChatsForUserQuery,
  useInsertChatMessageMutation,
  useUpdateChatByPkMutation,
  useUpdateChatMessageByPkMutation,
} from "graphql/__generated__/hooks";
import { useAuthUser } from "hooks/useAuthUser";
import { useAppSelector } from "hooks/useAppSelector";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import { ROLES } from "types/user";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CreateChatDialog } from "./CreateChatDialog";
import { useChatParticipantOptions } from "./useChatParticipantOptions";
import { getRealtimeSocket, publishChatActivity } from "realtime/socketClient";
import { useChatUnread } from "context/ChatUnreadContext";
import type { ChatMessageSocketPayload } from "hooks/useChatUnreadCounts";

type ChatMenuTarget = "header" | string;

export const Chat = () => {
  const theme = useTheme();
  const client = useApolloClient();
  const { user } = useAuthUser();
  const userId = user?.user_id ?? "";
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const selectedOrganization = useAppSelector(organizationSelectedSelector);
  const chatOrgId = useMemo(() => {
    if (user?.role === ROLES.ADMIN) {
      return selectedOrganization?.org_id;
    }
    return user?.org_id;
  }, [selectedOrganization?.org_id, user?.org_id, user?.role]);

  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const {
    participantOptions,
    participantsLoading,
    canSelectParticipant,
    contextMessage,
  } = useChatParticipantOptions({
    open: createOpen,
    currentUserId: userId,
    role: user?.role,
    orgIdForOrgWideList: chatOrgId,
  });

  const createChatBlockingMessage = useMemo(() => {
    if (user?.role === ROLES.ADMIN && !chatOrgId) {
      return "Select an organization in the sidebar to start a chat.";
    }
    return contextMessage;
  }, [user?.role, chatOrgId, contextMessage]);

  const [messageDraft, setMessageDraft] = useState("");
  const [editTitleOpen, setEditTitleOpen] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [titleDialogChatId, setTitleDialogChatId] = useState<string | null>(
    null,
  );
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editMessageText, setEditMessageText] = useState("");

  const [chatMenuAnchor, setChatMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [chatMenuTarget, setChatMenuTarget] = useState<ChatMenuTarget | null>(
    null,
  );
  const [messageMenuAnchor, setMessageMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [messageMenuId, setMessageMenuId] = useState<string | null>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<null | HTMLElement>(null);
  const messageInputRef = useRef<HTMLInputElement | null>(null);

  const {
    data: chatsData,
    loading: chatsLoading,
    error: chatsError,
    refetch: refetchChats,
  } = useGetChatsForUserQuery({
    variables: { userId },
    skip: !userId,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: messagesData,
    loading: messagesLoading,
    refetch: refetchMessages,
  } = useGetChatMessagesQuery({
    variables: { chatId: selectedChatId! },
    skip: !selectedChatId,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const [createChat, { loading: creating }] =
    useCreateChatWithParticipantsMutation();
  const [insertMessage, { loading: sending }] = useInsertChatMessageMutation();
  const [updateChat] = useUpdateChatByPkMutation();
  const [updateChatMessage] = useUpdateChatMessageByPkMutation();
  const [deleteChatByPk, { loading: deletingChat }] =
    useDeleteChatByPkMutation();
  const [deleteChatMessage] = useDeleteChatMessageByPkMutation();

  const chats = chatsData?.chat ?? [];
  const messages = messagesData?.chat_message ?? [];

  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const messagesScrollKey = useMemo(
    () =>
      messages
        .map(
          (m) =>
            `${m.chat_message_id}:${m.message}:${m.updated_at ?? m.created_at ?? ""}`,
        )
        .join("|"),
    [messages],
  );

  const { byChatId, markChatRead, setActiveChatId, socketConnected } =
    useChatUnread();

  const selectedChatIdRef = useRef(selectedChatId);
  selectedChatIdRef.current = selectedChatId;

  useEffect(() => {
    setActiveChatId(selectedChatId);
    return () => setActiveChatId(null);
  }, [selectedChatId, setActiveChatId]);

  useEffect(() => {
    if (!selectedChatId || messagesLoading) return;
    const msgsForChat = messages.filter((m) => m.chat_id === selectedChatId);
    if (messages.length > 0 && msgsForChat.length !== messages.length) return;
    markChatRead(selectedChatId, msgsForChat);
  }, [selectedChatId, messages, messagesLoading, markChatRead]);

  /** Pin scroll to the latest message on send, receive, edit, or thread switch. */
  useLayoutEffect(() => {
    if (!selectedChatId) return;
    if (messagesLoading && messages.length === 0) return;
    const el = messagesContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [selectedChatId, messagesScrollKey, messagesLoading, messages.length]);

  const chatIdsKey = useMemo(
    () => [...(chatsData?.chat ?? []).map((c) => c.chat_id)].sort().join(","),
    [chatsData?.chat],
  );

  const selectedChat = useMemo(
    () => chats.find((c) => c.chat_id === selectedChatId) ?? null,
    [chats, selectedChatId],
  );

  useEffect(() => {
    const s = getRealtimeSocket();
    if (!s || !socketConnected) return;
    const onChatMessage = (payload: ChatMessageSocketPayload) => {
      const chatId = payload.chatId;
      const uid = userIdRef.current;
      if (!chatId || !uid) return;

      void client.query({
        query: GetChatsForUserDocument,
        variables: { userId: uid },
        fetchPolicy: "network-only",
      });

      if (chatId === selectedChatIdRef.current) {
        void client.query({
          query: GetChatMessagesDocument,
          variables: { chatId },
          fetchPolicy: "network-only",
        });
      }
    };
    s.on("chat:message", onChatMessage);
    return () => {
      s.off("chat:message", onChatMessage);
    };
  }, [client, socketConnected]);

  useEffect(() => {
    if (!socketConnected) return;
    const s = getRealtimeSocket();
    if (!s || !chatIdsKey) return;
    const ids = chatIdsKey.split(",");
    const join = () => {
      ids.forEach((id) => {
        if (id) s.emit("chat:join", id);
      });
    };
    join();
    s.on("connect", join);
    return () => {
      s.off("connect", join);
    };
  }, [socketConnected, chatIdsKey]);

  const afterMutation = useCallback(async () => {
    await refetchChats();
    if (selectedChatId) await refetchMessages();
  }, [refetchChats, refetchMessages, selectedChatId]);

  const closeChatMenu = () => {
    setChatMenuAnchor(null);
    setChatMenuTarget(null);
  };

  const closeMessageMenu = () => {
    setMessageMenuAnchor(null);
    setMessageMenuId(null);
  };

  const resolveMenuChatId = (): string | null => {
    if (chatMenuTarget === "header") return selectedChatId;
    if (chatMenuTarget && chatMenuTarget !== "header") return chatMenuTarget;
    return null;
  };

  const handleCreateChat = async (title: string, otherUserId: string) => {
    if (!userId) return;
    const res = await createChat({
      variables: {
        title,
        userId1: userId,
        userId2: otherUserId,
      },
    });
    const newId = res.data?.insert_chat_one?.chat_id;
    setCreateOpen(false);
    await refetchChats();
    if (newId) {
      setSelectedChatId(newId);
      publishChatActivity(newId, [userId, otherUserId]);
    }
  };

  const handleSendMessage = async () => {
    const text = messageDraft.trim();
    if (!selectedChatId || !userId || !text) return;
    const memberIds =
      selectedChat?.chat_users
        ?.map((cu) => cu.user_id)
        .filter((id): id is string => Boolean(id)) ?? [];
    const insertRes = await insertMessage({
      variables: {
        object: {
          chat_id: selectedChatId,
          user_id: userId,
          message: text,
        },
      },
    });
    const newMessageId =
      insertRes.data?.insert_chat_message_one?.chat_message_id;
    setMessageDraft("");
    await afterMutation();
    publishChatActivity(selectedChatId, memberIds, {
      senderId: userId,
      messageId: newMessageId ?? undefined,
    });
  };

  const handleEmojiClick = useCallback((emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    const el = messageInputRef.current;
    setEmojiAnchor(null);
    if (!el) {
      setMessageDraft((d) => d + emoji);
      return;
    }
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    setMessageDraft((prev) => {
      const next = prev.slice(0, start) + emoji + prev.slice(end);
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + emoji.length;
        el.setSelectionRange(pos, pos);
      });
      return next;
    });
  }, []);

  const handleSaveTitle = async () => {
    const chatId = titleDialogChatId ?? selectedChatId;
    const t = editTitle.trim();
    if (!chatId || !t) return;
    const chat = chats.find((c) => c.chat_id === chatId);
    const memberIds =
      chat?.chat_users
        ?.map((cu) => cu.user_id)
        .filter((id): id is string => Boolean(id)) ?? [];
    await updateChat({
      variables: {
        chatId,
        set: { title: t },
      },
    });
    setEditTitleOpen(false);
    setTitleDialogChatId(null);
    await refetchChats();
    publishChatActivity(chatId, memberIds);
  };

  const handleDeleteChat = async (chatId: string) => {
    closeChatMenu();
    if (
      !window.confirm(
        "Delete this chat and all messages for everyone? This cannot be undone.",
      )
    ) {
      return;
    }
    const chat = chats.find((c) => c.chat_id === chatId);
    const memberIds =
      chat?.chat_users
        ?.map((cu) => cu.user_id)
        .filter((id): id is string => Boolean(id)) ?? [];
    await deleteChatByPk({ variables: { chatId } });
    if (selectedChatId === chatId) setSelectedChatId(null);
    await refetchChats();
    publishChatActivity(chatId, memberIds);
  };

  const handleDeleteMessage = async (messageId: string) => {
    closeMessageMenu();
    if (!window.confirm("Delete this message?")) return;
    const memberIds =
      selectedChat?.chat_users
        ?.map((cu) => cu.user_id)
        .filter((id): id is string => Boolean(id)) ?? [];
    await deleteChatMessage({ variables: { chatMessageId: messageId } });
    await afterMutation();
    if (selectedChatId) publishChatActivity(selectedChatId, memberIds);
  };

  const handleSaveMessageEdit = async () => {
    if (!editingMessageId) return;
    const text = editMessageText.trim();
    if (!text) return;
    const memberIds =
      selectedChat?.chat_users
        ?.map((cu) => cu.user_id)
        .filter((id): id is string => Boolean(id)) ?? [];
    await updateChatMessage({
      variables: {
        chatMessageId: editingMessageId,
        set: { message: text },
      },
    });
    setEditingMessageId(null);
    setEditMessageText("");
    await afterMutation();
    if (selectedChatId) publishChatActivity(selectedChatId, memberIds);
  };

  const openRenameDialog = (chatId: string) => {
    const c = chats.find((x) => x.chat_id === chatId);
    setEditTitle(c?.title ?? "");
    setTitleDialogChatId(chatId);
    setEditTitleOpen(true);
    closeChatMenu();
  };

  const openHeaderChatMenu = (e: React.MouseEvent<HTMLElement>) => {
    setChatMenuAnchor(e.currentTarget);
    setChatMenuTarget("header");
  };

  const openListChatMenu = (
    e: React.MouseEvent<HTMLElement>,
    chatId: string,
  ) => {
    e.stopPropagation();
    setChatMenuAnchor(e.currentTarget);
    setChatMenuTarget(chatId);
  };

  const onChatMenuRename = () => {
    const id = resolveMenuChatId();
    if (id) openRenameDialog(id);
  };

  const onChatMenuDelete = () => {
    const id = resolveMenuChatId();
    if (id) void handleDeleteChat(id);
  };

  const openMessageMenu = (e: React.MouseEvent<HTMLElement>, msgId: string) => {
    e.stopPropagation();
    setMessageMenuAnchor(e.currentTarget);
    setMessageMenuId(msgId);
  };

  const startEditMessage = (msgId: string, text: string) => {
    setEditingMessageId(msgId);
    setEditMessageText(text);
    closeMessageMenu();
  };

  return (
    <MainLayoutContainer title="Chat" loading={chatsLoading && !chatsData}>
      {chatsError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {chatsError.message}
        </Typography>
      )}

      <Box
        sx={{
          maxWidth: 1280,
          width: "100%",
          mx: "auto",
          flex: "1 1 0%",
          minHeight: 0,
          height: { xs: "auto", md: "calc(100vh - 280px)" },
          maxHeight: { xs: "none", md: "calc(100vh - 280px)" },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            flex: "1 1 0%",
            minHeight: 0,
            height: "100%",
            alignItems: "stretch",
          }}
        >
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              minWidth: 0,
              height: { xs: "auto", md: "100%" },
              maxHeight: { xs: "none", md: "100%" },
            }}
          >
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: { xs: 280, md: 0 },
                maxHeight: { md: "100%" },
                overflow: "hidden",
              }}
            >
              <CardHeader
                title="Conversations"
                action={
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={() => setCreateOpen(true)}
                  >
                    New
                  </Button>
                }
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ flex: 1, overflow: "auto", pt: 1 }}>
                {chats.length === 0 && !chatsLoading ? (
                  <Typography color="text.secondary" variant="body2">
                    No chats yet. Create one to start messaging.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {chats.map((c) => (
                      <Card
                        key={c.chat_id}
                        variant={
                          selectedChatId === c.chat_id
                            ? "elevation"
                            : "outlined"
                        }
                        elevation={selectedChatId === c.chat_id ? 2 : 0}
                        sx={{
                          cursor: "pointer",
                          transition: "box-shadow 0.2s",
                          "&:hover": {
                            boxShadow: 1,
                          },
                        }}
                        onClick={() => setSelectedChatId(c.chat_id)}
                      >
                        <CardHeader
                          title={
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ width: "100%", minWidth: 0, pr: 0.5 }}
                            >
                              <Typography
                                variant="subtitle2"
                                noWrap
                                sx={{ flex: 1, minWidth: 0 }}
                              >
                                {c.title || "Untitled chat"}
                              </Typography>
                              {(byChatId[c.chat_id] ?? 0) > 0 && (
                                <Chip
                                  size="small"
                                  color="error"
                                  label={byChatId[c.chat_id]}
                                  sx={{
                                    flexShrink: 0,
                                    height: 22,
                                    minWidth: 22,
                                    "& .MuiChip-label": { px: 0.75 },
                                  }}
                                />
                              )}
                            </Stack>
                          }
                          subheader={
                            c.updated_at
                              ? `Updated ${new Date(c.updated_at).toLocaleString()}`
                              : undefined
                          }
                          subheaderTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                          }}
                          action={
                            <IconButton
                              size="small"
                              aria-label="Chat options"
                              onClick={(e) => openListChatMenu(e, c.chat_id)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          }
                          sx={{
                            py: 1,
                            "& .MuiCardHeader-action": {
                              alignSelf: "center",
                              mr: 0,
                            },
                          }}
                        />
                      </Card>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid
            size={{ xs: 12, md: 8 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              minWidth: 0,
              height: { xs: "auto", md: "100%" },
              maxHeight: { xs: "none", md: "100%" },
            }}
          >
            {!selectedChatId ? (
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: { xs: 240, md: "100%" },
                }}
              >
                <CardContent>
                  <Typography color="text.secondary" align="center">
                    Select a conversation or create a new chat.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: { xs: 400, md: 0 },
                  minWidth: 0,
                  maxHeight: { md: "100%" },
                  overflow: "hidden",
                }}
              >
                <CardHeader
                  title={
                    <Typography variant="h6" component="h2">
                      {selectedChat?.title || "Chat"}
                    </Typography>
                  }
                  action={
                    <IconButton
                      aria-label="Conversation options"
                      onClick={openHeaderChatMenu}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  }
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    py: 1.5,
                  }}
                />

                <CardContent
                  ref={messagesContainerRef}
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    py: 2,
                    minHeight: 0,
                  }}
                >
                  {messagesLoading && messages.length === 0 ? (
                    <Typography color="text.secondary">
                      Loading messages…
                    </Typography>
                  ) : (
                    messages.map((m) => {
                      const isMine = m.user_id === userId;
                      if (editingMessageId === m.chat_message_id) {
                        return (
                          <Box key={m.chat_message_id} sx={{ mb: 2 }}>
                            <TextField
                              fullWidth
                              multiline
                              size="small"
                              value={editMessageText}
                              onChange={(e) =>
                                setEditMessageText(e.target.value)
                              }
                            />
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                              <Button
                                size="small"
                                onClick={handleSaveMessageEdit}
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                onClick={() => {
                                  setEditingMessageId(null);
                                  setEditMessageText("");
                                }}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          </Box>
                        );
                      }
                      return (
                        <Box
                          key={m.chat_message_id}
                          sx={{
                            mb: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: isMine ? "flex-end" : "flex-start",
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="flex-start"
                            spacing={0.5}
                            sx={{
                              maxWidth: "92%",
                              justifyContent: isMine
                                ? "flex-end"
                                : "flex-start",
                            }}
                          >
                            <Card
                              elevation={0}
                              variant="outlined"
                              sx={{
                                px: 1.5,
                                py: 1,
                                bgcolor: isMine ? "primary.light" : "grey.100",
                                borderColor: isMine
                                  ? "primary.light"
                                  : "divider",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isMine
                                    ? "primary.contrastText"
                                    : "text.secondary",
                                  opacity: isMine ? 0.85 : 1,
                                }}
                              >
                                {m.user?.name || m.user?.email || "User"}
                                {m.created_at
                                  ? ` · ${new Date(m.created_at).toLocaleString()}`
                                  : ""}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.5,
                                  color: isMine
                                    ? "primary.contrastText"
                                    : "text.primary",
                                }}
                              >
                                {m.message}
                              </Typography>
                            </Card>
                            {isMine && (
                              <IconButton
                                size="small"
                                aria-label="Message options"
                                onClick={(e) =>
                                  openMessageMenu(e, m.chat_message_id)
                                }
                                sx={{ mt: 0.25, flexShrink: 0 }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        </Box>
                      );
                    })
                  )}
                </CardContent>

                <Box
                  sx={{
                    flexShrink: 0,
                    flexGrow: 0,
                    px: 2,
                    pb: 2,
                    pt: 2.5,
                    borderTop: 1,
                    borderColor: "divider",
                    minWidth: 0,
                    bgcolor: "background.paper",
                    zIndex: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Type a message…"
                      value={messageDraft}
                      onChange={(e) => setMessageDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      inputRef={messageInputRef}
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                    <IconButton
                      aria-label="Emoji"
                      onClick={(e) => setEmojiAnchor(e.currentTarget)}
                      sx={{ flexShrink: 0 }}
                    >
                      <InsertEmoticonOutlinedIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={handleSendMessage}
                      disabled={sending || !messageDraft.trim()}
                      aria-label="Send"
                      sx={{ flexShrink: 0 }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Stack>
                </Box>
              </Card>
            )}
          </Grid>
        </Grid>
      </Box>

      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { overflow: "hidden", mt: -0.5 },
          },
        }}
      >
        <EmojiPicker
          onEmojiClick={handleEmojiClick}
          theme={
            theme.palette.mode === "dark"
              ? EmojiPickerTheme.DARK
              : EmojiPickerTheme.LIGHT
          }
          width={320}
          height={380}
        />
      </Popover>

      <Menu
        anchorEl={chatMenuAnchor}
        open={Boolean(chatMenuAnchor)}
        onClose={closeChatMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={onChatMenuRename}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Rename
        </MenuItem>
        <MenuItem
          onClick={onChatMenuDelete}
          disabled={deletingChat}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={messageMenuAnchor}
        open={Boolean(messageMenuAnchor)}
        onClose={closeMessageMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            const id = messageMenuId;
            const msg = messages.find((x) => x.chat_message_id === id);
            if (id && msg) startEditMessage(id, msg.message ?? "");
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (messageMenuId) void handleDeleteMessage(messageMenuId);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <CreateChatDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        currentUserId={userId}
        canSelectParticipant={canSelectParticipant}
        participantOptions={participantOptions}
        participantsLoading={participantsLoading}
        blockingMessage={createChatBlockingMessage}
        onSubmit={handleCreateChat}
        submitting={creating}
      />

      <Dialog
        open={editTitleOpen}
        onClose={() => {
          setEditTitleOpen(false);
          setTitleDialogChatId(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Rename chat</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditTitleOpen(false);
              setTitleDialogChatId(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveTitle}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayoutContainer>
  );
};
